/**
 * THE MILESTONE CALLOUT PACKER (0124 readability rework, Vinnie 29 Oct) — the pure
 * geometry that keeps a steering lane legible however its diamonds cluster.
 *
 * The old lane centred each label on its diamond and only alternated above / below, so
 * a dense row (five checkpoints in seven weeks, a go-live landing two days from a
 * sign-off) overprinted into an unreadable jumble. This replaces that with a greedy
 * tier packer: diamonds stay pinned on their real dates, and each label becomes a
 * compact callout CHIP assigned to the nearest collision-free tier above or below the
 * axis, tied to its diamond by a leader line. A lane that still cannot fit every chip
 * in its tier budget collapses the remainder into a "+N more" count — the diamonds
 * stay on the axis, so nothing is ever lost.
 *
 * This file is DOM-free on purpose: it takes each node's centre-x and measured chip
 * width in px and returns where every chip goes, so the algorithm is unit-testable
 * without a browser (scripts/test-milestone-pack.ts). The component measures the real
 * widths, calls this, and draws the result.
 */

/** A diamond's centre and its chip's measured width, both in px from the track's left. */
export interface PackNode {
  readonly cx: number;
  readonly w: number;
}

export type CalloutSide = 'up' | 'dn';

/** Where one chip lands. `overflow` chips have no free tier and collapse into the count. */
export interface Placement {
  readonly side: CalloutSide;
  readonly tier: number; // 1..maxTier
  readonly overflow: boolean;
}

export interface PackOptions {
  /** The minimum horizontal gap between two chips sharing a tier (px). */
  readonly gut?: number;
  /** How many tiers each side of the axis may use before a chip overflows. */
  readonly maxTier?: number;
}

/** The pixel geometry the lane layout snaps to — one place, so the component that draws
 *  the lane and the test that checks its height read the same numbers. */
export const PACK_GEOM = { ROW: 23, AXIS: 24, PADV: 7, GUT: 8, MAX_TIER: 2 } as const;

/**
 * Assign every node a collision-free tier above / below the axis, nearest-first (up1,
 * dn1, up2, dn2…). A node that finds no free tier within the budget is marked overflow.
 * Placements come back aligned to the INPUT order, so the caller keeps its own ordering;
 * internally the nodes are considered left-to-right so "nearest free tier" is stable.
 * Pure — no DOM, no mutation of the input.
 */
export function packCallouts(nodes: readonly PackNode[], opts: PackOptions = {}): Placement[] {
  const gut = opts.gut ?? PACK_GEOM.GUT;
  const maxTier = opts.maxTier ?? PACK_GEOM.MAX_TIER;

  // Tiers tried nearest-first: the tier closest to the axis on each side, then the next.
  const slots: { side: CalloutSide; tier: number }[] = [];
  for (let t = 1; t <= maxTier; t++) {
    slots.push({ side: 'up', tier: t });
    slots.push({ side: 'dn', tier: t });
  }
  const occupied = new Map<string, { lo: number; hi: number }[]>();
  slots.forEach((s) => occupied.set(`${s.side}${s.tier}`, []));

  const out = new Array<Placement>(nodes.length);
  const order = nodes.map((n, i) => ({ n, i })).sort((a, b) => a.n.cx - b.n.cx);
  for (const { n, i } of order) {
    const half = n.w / 2;
    const lo = n.cx - half;
    const hi = n.cx + half;
    let placed: { side: CalloutSide; tier: number } | null = null;
    for (const s of slots) {
      const used = occupied.get(`${s.side}${s.tier}`)!;
      const clash = used.some((r) => !(hi + gut <= r.lo || lo - gut >= r.hi));
      if (!clash) {
        used.push({ lo, hi });
        placed = s;
        break;
      }
    }
    out[i] = placed
      ? { side: placed.side, tier: placed.tier, overflow: false }
      : { side: 'dn', tier: maxTier, overflow: true };
  }
  return out;
}

/** How many tiers a lane actually used on each side, and whether anything overflowed —
 *  the lane's height is derived from this, so a simple one-chip row stays compact. */
export interface LaneMetrics {
  readonly tiersUp: number;
  readonly tiersDn: number;
  readonly hasOverflow: boolean;
}

export function laneMetrics(places: readonly Placement[]): LaneMetrics {
  let tiersUp = 0;
  let tiersDn = 0;
  let hasOverflow = false;
  for (const p of places) {
    if (p.overflow) {
      hasOverflow = true;
      continue;
    }
    if (p.side === 'up') tiersUp = Math.max(tiersUp, p.tier);
    else tiersDn = Math.max(tiersDn, p.tier);
  }
  return { tiersUp, tiersDn, hasOverflow };
}

/** The lane's total height (px): padding, the up tiers, the axis band, the down tiers,
 *  one extra row for the "+N more" bubble when there is overflow, and padding. */
export function laneHeight(m: LaneMetrics): number {
  const tiersDnEff = m.tiersDn + (m.hasOverflow ? 1 : 0);
  return PACK_GEOM.PADV + m.tiersUp * PACK_GEOM.ROW + PACK_GEOM.AXIS + tiersDnEff * PACK_GEOM.ROW + PACK_GEOM.PADV;
}

/** The y of the diamond band's centre (px) — where every diamond and leader origin sits. */
export function diamondY(m: LaneMetrics): number {
  return PACK_GEOM.PADV + m.tiersUp * PACK_GEOM.ROW + PACK_GEOM.AXIS / 2;
}

/** A chip's centre-y (px) for its placement, measured from the lane top via `diamondY`. */
export function chipY(p: Placement, dY: number): number {
  const { ROW, AXIS } = PACK_GEOM;
  return p.side === 'up'
    ? dY - AXIS / 2 - (p.tier - 1) * ROW - ROW / 2
    : dY + AXIS / 2 + (p.tier - 1) * ROW + ROW / 2;
}

/** The "+N more" bubble's centre-y (px) — the reserved row below the last used down tier. */
export function overflowY(m: LaneMetrics, dY: number): number {
  const { ROW, AXIS } = PACK_GEOM;
  return dY + AXIS / 2 + m.tiersDn * ROW + ROW / 2;
}
